// ===== Types =====

export interface AdvFundUserRecord {
    id: number;
    user_id: number;
    autopay_amount: number;
    autopay: boolean;
    created_at: string;
    deleted_at: string | null;
    total?: {
        total_paid: number;
        share_percent: number;
        leads_count: number;
    } | null;
    user: {
        id: number;
        secret_cookie: string;
        name: string;
        surname: string;
        email: string;
        photo: string;
    } | null;
}

export interface AdvFundPaymentRecord {
    id: number;
    user_id: number;
    amount: string;
    type: "manual" | "auto";
    currency: "USD" | "USDT" | "MIX";
    total: string;
    created_at: string;
    user: {
        id: number;
        secret_cookie: string;
        name: string;
        surname: string;
        photo: string;
    } | null;
}

export interface AdvFundAdminSettings {
    min_amount: number;
}

export interface AdvFundMonthlyStats {
    total_users: number;
    total_collected: number;
    total_leads: number;
}

export interface AdvFundHistoricalStat {
    year: number;
    month: number;
    label: string;
    total_users: number;
    total_collected: number;
}

export interface AdvFundHistoricalStatsResponse {
    data: AdvFundHistoricalStat[];
}

export interface AdvFundUsersResponse {
    data: AdvFundUserRecord[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
}

export interface AdvFundPaymentsResponse {
    data: AdvFundPaymentRecord[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
}

export interface AdvFundChannel {
    id: number;
    name: string;
    description: string | null;
    ref_code: string;
    total_paid: string;
    total_registrations: number;
    is_active: boolean;
    registration_logs_count: number;
    click_logs_count: number;
    unique_clicks_count: number;
    conversion_rate: number;
}

export interface AdvFundExpenseRecord {
    id: number;
    channel_id: number;
    amount: string;
    description: string;
    expense_date: string;
    registrations_count: number;
    created_by: number;
    created_at: string;
    channel?: AdvFundChannel;
}

export interface AdvFundClickTimelineRecord {
    date: string;
    total_clicks: number;
    unique_clicks: number;
    conversions: number;
}

export interface AdvFundChannelsResponse {
    data: AdvFundChannel[];
}

export interface AdvFundExpensesResponse {
    data: AdvFundExpenseRecord[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
}

export interface CreateAdvFundChannelRequest {
    name: string;
    description?: string;
    is_active?: boolean;
}

export interface UpdateAdvFundChannelRequest {
    name?: string;
    description?: string;
    is_active?: boolean;
}

export interface AdvFundUsersParams {
    page?: number;
    per_page?: number;
    search?: string;
    status?: "active" | "deleted" | "";
}

export interface AdvFundPaymentsParams {
    page?: number;
    per_page?: number;
    user_id?: number;
    type?: "manual" | "auto" | "";
    date_from?: string;
    date_to?: string;
}

export interface AdvFundExpensesParams {
    page?: number;
    per_page?: number;
    channel_id?: number;
}

export interface CreateAdvFundExpenseRequest {
    amount: number;
    description: string;
    expense_date: string;
    registrations_count?: number;
}

// ===== Endpoints =====

export const advertisingFundEndpoints = {
    getAdvFundSettings: {
        query: () => ({
            url: "/admin/advertising-fund/settings",
        }),
    },
    updateAdvFundSettings: {
        query: (data: AdvFundAdminSettings) => ({
            url: "/admin/advertising-fund/settings",
            method: "PUT",
            body: data,
        }),
    },
    getAdvFundUsers: {
        query: (params: AdvFundUsersParams) => ({
            url: "/admin/advertising-fund/users",
            params,
        }),
    },
    getAdvFundPayments: {
        query: (params: AdvFundPaymentsParams) => ({
            url: "/admin/advertising-fund/payments",
            params,
        }),
    },
    getAdvFundMonthlyStats: {
        query: () => ({
            url: "/admin/advertising-fund/monthly-stats",
        }),
    },
    getAdvFundHistoricalStats: {
        query: () => ({
            url: "/admin/advertising-fund/historical-stats",
        }),
    },
    getAdvFundChannels: {
        query: () => ({
            url: "/admin/advertising-fund/channels",
        }),
    },
    createAdvFundChannel: {
        query: (data: CreateAdvFundChannelRequest) => ({
            url: "/admin/advertising-fund/channels",
            method: "POST",
            body: data,
        }),
    },
    updateAdvFundChannel: {
        query: ({ id, ...data }: UpdateAdvFundChannelRequest & { id: number }) => ({
            url: `/admin/advertising-fund/channels/${id}`,
            method: "PUT",
            body: data,
        }),
    },
    deleteAdvFundChannel: {
        query: (id: number) => ({
            url: `/admin/advertising-fund/channels/${id}`,
            method: "DELETE",
        }),
    },
    getAdvFundExpenses: {
        query: (params: AdvFundExpensesParams) => ({
            url: "/admin/advertising-fund/expenses",
            params,
        }),
    },
    getAdvFundChannelExpenses: {
        query: (id: number) => ({
            url: `/admin/advertising-fund/channels/${id}/expenses`,
        }),
    },
    createAdvFundExpense: {
        query: ({ channelId, ...data }: CreateAdvFundExpenseRequest & { channelId: number }) => ({
            url: `/admin/advertising-fund/channels/${channelId}/expenses`,
            method: "POST",
            body: data,
        }),
    },
    getAdvFundChannelClickTimeline: {
        query: (id: number) => ({
            url: `/admin/advertising-fund/channels/${id}/click-timeline`,
        }),
    },
};

export const advertisingFundTags = ["AdvFund", "AdvFundSettings", "AdvFundChannels"];
